import * as v from "valibot";
import { compoundKey } from "@supply25/database/key";
import {
  AnswerSchema,
  InfoOutcomeSchema,
  PassOutcomeSchema,
  ScoreOutcomeSchema,
} from "./assessment.ts";

export const ImprovementActionDecision = {
  ACCEPT: "ACCEPT",
  REJECT: "REJECT",
} as const;

const improvementActionEntries = {
  // @todo: Support literals in compound keys
  // tk: `${ItemType.IMPROVEMENT_ACTION}~${ItemType.QUESTION}#${string}`;
  tk: v.string(),
  title: v.string(),
  answer: v.string(),
  authorId: v.string(),
  lastUpdated: v.string(),
  createdDate: v.string(),
  type: v.literal("IMPROVEMENT_ACTION"),
  attempt: v.number(),
  decision: v.exactOptional(v.enum(ImprovementActionDecision)),
  decisionReason: v.exactOptional(v.string()),
  decisionAuthorId: v.exactOptional(v.string()),
  decisionDate: v.exactOptional(v.string()),
  completionDate: v.exactOptional(v.string()),
};

// The improvement action shape after parsing with Valibot
export const ImprovementActionOutSchema = v.object({
  ...improvementActionEntries,
  organisationId: v.string(),
  workspaceId: v.string(),
  questionId: v.string(),
  urlId: v.string(),
});

export const ImprovementActionSchema = v.pipe(
  v.object({
    pk: compoundKey("ORGANISATION", "WORKSPACE"),
    // @todo: Support literals in compound keys
    // sk: `${ItemType.IMPROVEMENT_ACTION}~${Prefix.URL_ID}#${string}~${ItemType.QUESTION}#${string}`,
    sk: v.string(),
    ...improvementActionEntries,
  }),
  v.transform(({ pk, sk, ...rest }) => {
    const [, [, urlId], [, questionId]] = sk
      .split("~")
      .map((pair) => pair.split("#"));
    return {
      ...rest,
      organisationId: pk.ORGANISATION,
      workspaceId: pk.WORKSPACE,
      questionId,
      urlId,
    } satisfies v.InferInput<typeof ImprovementActionOutSchema>;
  }),
);

export const ImprovementActionsSchema = v.array(ImprovementActionSchema);

export type ImprovementAction = v.InferOutput<typeof ImprovementActionSchema>;
export type ImprovementActionIn = v.InferInput<typeof ImprovementActionSchema>;

const OutcomeSchema = v.union([
  InfoOutcomeSchema,
  PassOutcomeSchema,
  ScoreOutcomeSchema,
]);

export const assessmentResultKey = {
  pk: compoundKey("ORGANISATION", "WORKSPACE"),
  sk: compoundKey("URLID", "RESULT", "ATTEMPT"),
};

const resultEntries = {
  ...assessmentResultKey,
  // @todo: compoundKey can't do literal values
  // tk needs to support `TENDERPACKAGE#123~RESULT`
  tk: v.string(),
  // @todo: compoundKey can't do enum values
  // needs to support `${"BUYER" | "SUPPLIER"}~TPCOLLECTION#123~TPASSESSMENT#abc`;
  assessment: v.string(),
  organisationName: v.string(),
  authorId: v.string(),
  outcome: OutcomeSchema,
  createdDate: v.string(),
  type: v.literal("RESULT"),
  buyerOrganisationId: v.string(),
  buyerWorkspaceId: v.string(),
};

const CompletedResultSchema = v.object({
  ...resultEntries,
  override: v.exactOptional(v.literal(false)),
  answers: v.array(AnswerSchema),
  failedAnswers: v.exactOptional(v.array(AnswerSchema)),
  score: v.exactOptional(v.number()),
  improvementPlan: v.exactOptional(ImprovementActionsSchema),
  applicant: v.exactOptional(v.array(AnswerSchema)),
  acknowledgeMessage: v.exactOptional(v.string()),
});

// An organisation admin replacing the outcome
// Room for a couple of sentences, and short enough to fit on the cover of the
// buyer's answers PDF
export const OVERRIDE_REASON_MAX_LENGTH = 300;

const OverriddenResultSchema = v.object({
  ...resultEntries,
  override: v.literal(true),
  reason: v.string(),
  // The outcome at the time of the override, kept for audit
  overriddenFrom: OutcomeSchema,
});

export const AssessmentResultSchema = v.pipe(
  v.variant("override", [CompletedResultSchema, OverriddenResultSchema]),
  v.transform(({ pk, sk, ...rest }) => ({
    ...rest,
    organisationId: pk.ORGANISATION,
    workspaceId: pk.WORKSPACE,
    urlId: sk.URLID,
    attempt: Number(sk.ATTEMPT),
  })),
);

export const AssessmentResultsSchema = v.array(AssessmentResultSchema);

export type AssessmentResult = v.InferOutput<typeof AssessmentResultSchema>;

// When writing a Result to the DB we want the _input_ shape with the pk/sk etc
export type AssessmentResultIn = v.InferInput<typeof AssessmentResultSchema>;
